import React, { useContext, useEffect, useState } from 'react';
import './Partform.scss';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Upload,
  Row,
  Col,
  Select,
  Divider,
  Tag,
  Popconfirm,
  Checkbox,
  Tooltip
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form';
import { IfileUploadHookReturn } from 'hooks/FileUpload.hook';
import { RowProps } from 'antd/lib/row';
import { ColProps } from 'antd/lib/col';
import { observer } from 'mobx-react';
import { rootContext } from 'stores';
import { ISubModelInputs } from 'pages/Createpart/Createpart';
import Editregion from 'components/Editregion/Editregion';
import EditPatternType from 'components/EditPatternTypes/EditPatternType';

export type IPartFormProps = {
  autoIncrement: boolean;
  setAutoIncrement: any;
  form: FormInstance;
  onSubmit: (values: any) => void;
  loading?: boolean;
  partSvgFileUploadHook: IfileUploadHookReturn;
  carModelFileUploadHook: IfileUploadHookReturn;
  mode: 'add' | 'edit';
  regionIds: any;
  subModelInputs: ISubModelInputs[];
  setSubModelInputs: React.Dispatch<React.SetStateAction<ISubModelInputs[]>>;
  saveFileContent: (as: string) => void;
};
const { Option } = Select;
const d = new Date();
const currentYear = d.getFullYear();
const Partform: React.FC<IPartFormProps> = observer(
  ({
    form,
    setAutoIncrement,
    autoIncrement,
    onSubmit,
    loading = false,
    partSvgFileUploadHook,
    carModelFileUploadHook,
    regionIds,
    mode,
    saveFileContent,
    subModelInputs,
    setSubModelInputs
  }) => {
    const [region, setRegion] = useState('');
    const [pattern, setPattern] = useState('');
    const [checkAll, setCheckAll] = useState(false);
    const isEdit = mode === 'edit';

    const {
      BrandStore: {
        getBrands,
        state: { data, ui }
      },
      PartsStore: {
        state: {
          data: { editPart }
        }
      },
      RegionStore: {
        getRegions,
        addRegion,
        deleteRegion,
        state: {
          data: regionData,
          data: { regions },
          ui: { getRegionsLoading, addRegionsLoading, deleteRegionLoading }
        },
        setState
      },
      PatternStore: {
        getPattern,
        createPattern,
        deletePattern,
        state: {
          data: patternData,
          data: { patterns },
          ui: { getPatternLoading, addPatternLoading, deletePatternLoading }
        },
        setState: patternSetState
      }
    } = useContext(rootContext);

    const rowProp: RowProps = {
      justify: 'space-around',
      align: 'stretch'
    };
    const colProp: ColProps = {
      span: 10,
      xs: 10
    };
    useEffect(() => {
      getBrands();
      getPattern();
    }, [getBrands, getPattern]);
    useEffect(() => {
      getRegions();
    }, [getRegions]);
    const { RangePicker } = DatePicker;

    const validations = (rule: any, value: any) => {
      if (value && value?.trim()?.length === 0) {
        return Promise.reject('Whitespaces are not valid.');
      } else {
        return Promise.resolve(value?.trim() || '');
      }
    };

    const checkAllRegions = (checked: boolean) => {
      if (checked) {
        setCheckAll(true);
      } else {
        setCheckAll(false);
      }
      form.setFieldsValue({
        region: checked
          ? regionData.regions.map((item: any) => item.id)
          : regionIds
      });
    };

    useEffect(() => {
      if (regionData) {
        if (
          regionData.regions.length &&
          regionIds.length === regionData.regions.length
        ) {
          setCheckAll(true);
        } else {
          setCheckAll(false);
        }
      }
    }, [regionData, regionIds.length]);

    const handleSVGChange = (e: any, callback: any) => {
      const iframe: any = document.getElementById('svgViewer');

      iframe.src = '';
      iframe.src = '/viewer/viewer.html';
      const reader = new FileReader();
      let fileContent: any = null;
      let frameLoaded = false;

      iframe.onload = function() {
        frameLoaded = true;
        if (fileContent)
          iframe.contentWindow.InitializeViewer(fileContent, true, callback);
      };

      reader.onload = function(event: any) {
        fileContent = event?.target?.result;
        if (frameLoaded)
          iframe.contentWindow.InitializeViewer(fileContent, true, callback);
      };

      reader.readAsText(e.file);
    };

    return (
      <>
        <Editregion />
        <EditPatternType />
        <Form
          form={form}
          onFinish={onSubmit}
          layout="vertical"
          className="form_block_main"
        >
          <Row {...rowProp}>
            <Col {...colProp}>
              <Form.Item
                label="Name"
                name="name"
                rules={
                  isEdit
                    ? [{ validator: validations }]
                    : [
                        {
                          required: true,
                          message: 'Please Enter Name'
                        },
                        { validator: validations }
                      ]
                }
              >
                <Input placeholder="Name" />
              </Form.Item>
            </Col>
            <Col {...colProp}>
              <Form.Item
                label="Brand"
                name="brand"
                rules={
                  isEdit
                    ? []
                    : [
                        {
                          required: true,
                          message: 'Please Enter Brand'
                        }
                      ]
                }
              >
                <Select
                  loading={ui.getBrandsLoading}
                  placeholder="Select Brand"
                >
                  {data.brands?.map(({ brand, id }) => (
                    <Select.Option key={id} value={id}>
                      {brand}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col {...colProp}>
              <Form.Item
                label="Model"
                name="model"
                rules={
                  isEdit
                    ? [{ validator: validations }]
                    : [
                        {
                          required: true,
                          message: 'Please Enter Model'
                        },
                        { validator: validations }
                      ]
                }
              >
                <Input placeholder="Model" />
              </Form.Item>
            </Col>
            <Col {...colProp}>
              <Form.Item
                className="region-form-item"
                label="Pattern Type"
                name="pattern_type"
                rules={
                  isEdit
                    ? []
                    : [
                        {
                          required: true,
                          message: 'Please Select Pattern Type'
                        }
                      ]
                }
              >
                <Select
                  loading={getPatternLoading}
                  placeholder="Select Pattern"
                  optionLabelProp='label'
                  showSearch={false}
                  dropdownRender={menu => (
                    <div>
                      <div className="addRegionContainer">
                        <Input
                          style={{ flex: 'auto' }}
                          value={pattern}
                          placeholder="Add Pattern Type"
                          onChange={e => setPattern(e.target.value)}
                        />

                        <Button
                          loading={addPatternLoading}
                          onClick={async () => {
                            await createPattern({ name: pattern });
                            setPattern('');
                          }}
                          disabled={!pattern}
                        >
                          <PlusOutlined />
                        </Button>
                      </div>
                      <Divider style={{ margin: '4px 0' }} />
                      {menu}
                    </div>
                  )}
                >
                  {patterns?.map(({ name, id }) => (
                    <Select.Option
                      key={id}
                      value={name}
                      label={name}
                      className="regionOption"
                    >
                      <span>{name}</span>
                      <div>
                        <Button
                          onClick={e => {
                            // e.stopPropagation();
                            form.setFieldsValue({ pattern_type: '' });
                            patternSetState({
                              data: {
                                ...patternData,
                                editPatternData: { name, id }
                              }
                            });
                          }}
                        >
                          <EditOutlined />
                        </Button>
                        <Popconfirm
                          onConfirm={e => {
                            deletePattern(id);
                            form.setFieldsValue({ pattern_type: '' });
                          }}
                          onCancel={e => e?.stopPropagation()}
                          title="Are you sure？"
                          icon={
                            <QuestionCircleOutlined style={{ color: 'red' }} />
                          }
                        >
                          <Button
                            ghost={true}
                            className="deleteButton flexend"
                            onClick={e => {
                              e.stopPropagation();
                            }}
                            loading={id === deletePatternLoading}
                          >
                            <DeleteOutlined />
                          </Button>
                        </Popconfirm>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col {...colProp}>
              <Form.Item
                className="region-form-item"
                label={
                  <div className="region-label">
                    Region{' '}
                    <Checkbox
                      style={{ marginLeft: '10px' }}
                      onChange={e => checkAllRegions(e.target.checked)}
                      checked={checkAll}
                    >
                      International
                    </Checkbox>
                  </div>
                }
                name="region"
                rules={
                  isEdit
                    ? []
                    : [
                        {
                          required: true,
                          message: 'Please Enter Region'
                        }
                      ]
                }
              >
                <Select
                  tagRender={({ value }) => {
                    return (
                      <Tag>{regions.find(rg => rg.id === value)?.name}</Tag>
                    );
                  }}
                  loading={getRegionsLoading}
                  placeholder="Select Region"
                  mode="multiple"
                  showSearch={false}
                  dropdownRender={menu => (
                    <div>
                      <div className="addRegionContainer">
                        <Input
                          style={{ flex: 'auto' }}
                          value={region}
                          placeholder="Add Region"
                          onChange={e => setRegion(e.target.value)}
                        />

                        <Button
                          loading={addRegionsLoading}
                          onClick={async () => {
                            await addRegion({ name: region });
                            setRegion('');
                          }}
                          disabled={!region}
                        >
                          <PlusOutlined />
                        </Button>
                      </div>
                      <Divider style={{ margin: '4px 0' }} />
                      {menu}
                    </div>
                  )}
                >
                  {regions?.map(({ name, id }) => (
                    <Select.Option key={id} value={id} className="regionOption">
                      <span>{name}</span>
                      <div>
                        <Button
                          onClick={e => {
                            // e.stopPropagation();
                            setState({
                              data: {
                                ...regionData,
                                editRegionData: { name, id }
                              }
                            });
                          }}
                        >
                          <EditOutlined />
                        </Button>
                        <Popconfirm
                          onConfirm={e => {
                            deleteRegion(id);
                          }}
                          onCancel={e => e?.stopPropagation()}
                          title="Are you sure？"
                          icon={
                            <QuestionCircleOutlined style={{ color: 'red' }} />
                          }
                        >
                          <Button
                            ghost={true}
                            className="deleteButton flexend"
                            onClick={e => {
                              e.stopPropagation();
                            }}
                            loading={id === deleteRegionLoading}
                          >
                            <DeleteOutlined />
                          </Button>
                        </Popconfirm>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col {...colProp}>
              <Form.Item
                label={
                  <div className="region-label">
                    Making Year
                    <Tooltip title="If checked End Year will be auto incremented">
                      <Checkbox
                        defaultChecked={autoIncrement}
                        style={{ marginLeft: '75px' }}
                        onChange={e => setAutoIncrement(e.target.checked)}
                      >
                        Auto Increment Year
                      </Checkbox>
                    </Tooltip>
                  </div>
                }
                name="makingYear"
                rules={[
                  {
                    required: true,
                    message: 'Please Enter Making Year'
                  }
                ]}
              >
                <RangePicker
                  format="YYYY"
                  picker="year"
                  placeholder={['Start Year', 'End Year']}
                  disabledDate={date => date && date.year() > currentYear + 1}
                  disabled={[false, autoIncrement]}
                />
              </Form.Item>
            </Col>

            <Col {...colProp}>
              <Form.Item
                label="Part SVG Image"
                name="file"
                tooltip={'Part must be SVG file'}
                rules={
                  isEdit
                    ? []
                    : [
                        {
                          required: true,
                          message: 'Please Attach Image'
                        }
                      ]
                }
              >
                <Upload
                  key="part_image"
                  {...partSvgFileUploadHook}
                  accept="image/svg+xml"
                  onChange={e => handleSVGChange(e, saveFileContent)}
                >
                  <Button icon={<UploadOutlined />}>Click to upload</Button>
                </Upload>
              </Form.Item>
              <iframe
                id="svgViewer"
                src="/viewer/viewer.html"
                title="myFrame"
                height="270px"
                width="330px"
              ></iframe>
            </Col>

            <Col {...colProp}>
              <Form.Item
                label="Model Image"
                name="model_image"
                tooltip="All the submodel created will inherit this image unless passed"
                rules={[]}
              >
                <Upload
                  {...carModelFileUploadHook}
                  accept="image/x-png,image/jpeg,image/jpg"
                >
                  <Button icon={<UploadOutlined />}>Click to upload</Button>
                </Upload>
              </Form.Item>
            </Col>

            {/* Probably this will be needed
          <Col {...colProp}>
            <Form.Item label="Car Image" name="carImage">
              <Upload
                {...carModelFileUploadHook}
                accept="image/x-png,image/jpeg,image/svg+xml"
              >
                <Button icon={<UploadOutlined />}>Click to upload</Button>
              </Upload>
            </Form.Item>
          </Col> */}

            {/* {!isEdit && (
              <>
                <Col span={24} className="subModelContainer">
                  <Divider orientation="left">
                    Sub Models
                    {(!subModelInputs.some(
                      item => !item.subModel || !item.file
                    ) ||
                      subModelInputs.length === 0 ||
                      true) && (
                      <Button
                        type="link"
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none'
                        }}
                        // disabled={subModelInputs.some(
                        //   item => !item.subModel || !item.file
                        // )}
                        onClick={() => {
                          if (isSubModelsValid(subModelInputs, true)) {
                            setSubModelInputs(value => [
                              ...value,
                              { subModel: '' }
                            ]);
                          }
                        }}
                      >
                        <PlusSquareFilled />
                      </Button>
                    )}
                  </Divider>
                </Col>

                <Row align="middle" justify="center">
                  <Col span={24}>
                    {subModelInputs.map(
                      (item: ISubModelInputs, subModelindex: number) => (
                        <div className="subModelRow">
                          <Input
                            value={item.subModel}
                            placeholder="sub model"
                            className="inpuName"
                            onChange={e =>
                              setSubModelInputs(value =>
                                value.map((item, index) => {
                                  if (index === subModelindex) {
                                    item.subModel = e.target.value;
                                  }
                                  return item;
                                })
                              )
                            }
                          />
                          <Input
                            className="inputFile"
                            type="file"
                            accept=".jpg,.png,.jpeg,.gif"
                            placeholder="sub model file"
                            onChange={e =>
                              setSubModelInputs(value =>
                                value.map((item, index) => {
                                  if (index === subModelindex) {
                                    if (e.target.files && e.target.files[0]) {
                                      item.file = e.target.files[0];
                                    }
                                  }
                                  return item;
                                })
                              )
                            }
                          />
                          <Button
                            ghost={true}
                            className="deleteButton"
                            onClick={() =>
                              setSubModelInputs(values =>
                                values.filter((item, index) =>
                                  index === subModelindex ? false : true
                                )
                              )
                            }
                          >
                            <DeleteFilled />
                          </Button>
                        </div>
                      )
                    )}
                  </Col>
                </Row>
              </>
            )} */}
            <Col span={22} className="topGap">
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={loading}
                >
                  {isEdit ? 'Save' : 'Submit'}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </>
    );
  }
);

export default Partform;
